// BUG_REPORTER — TEMPORARY FEATURE. Delete this folder per docs/BUG_REPORTER.md.
//
// "Describe the issue with a marker": the captured screen with a box, an arrow,
// a scribble, or a black rectangle drawn over it.
//
// The black rectangle is not decoration. These captures contain live client
// pricing, vendor invoices and margins, and a submitted one is copied
// permanently into ClickUp's storage. Redaction is composited into the PIXELS
// before the blob is made — there is no layer to peel off afterwards.

import React, { forwardRef, useCallback, useEffect, useImperativeHandle, useRef, useState } from 'react';
import {
  INK,
  LINE_WIDTH,
  REDACT_FILL,
  arrowHeadPoints,
  normalizeRect,
  pruneShapes,
  toCanvasPoint,
  type Point,
  type Shape,
  type Tool,
} from './annotation';

export type AnnotationHandle = {
  /** The annotated image, ready to upload. Null if the browser refuses the encode. */
  toBlob: () => Promise<Blob | null>;
  hasMarks: () => boolean;
};

const TOOLS: Array<{ id: Tool; label: string; hint: string }> = [
  { id: 'rect', label: 'Box', hint: 'Draw a box around the problem' },
  { id: 'arrow', label: 'Arrow', hint: 'Point at the problem' },
  { id: 'pen', label: 'Draw', hint: 'Scribble freehand' },
  { id: 'redact', label: 'Black out', hint: 'Hide a price or figure before sending' },
];

function drawShape(ctx: CanvasRenderingContext2D, shape: Shape) {
  ctx.lineWidth = LINE_WIDTH;
  ctx.lineCap = 'round';
  ctx.lineJoin = 'round';
  ctx.strokeStyle = INK;

  if (shape.tool === 'redact') {
    const r = normalizeRect(shape.from, shape.to);
    ctx.fillStyle = REDACT_FILL;
    ctx.fillRect(r.x, r.y, r.width, r.height);
    return;
  }

  if (shape.tool === 'rect') {
    const r = normalizeRect(shape.from, shape.to);
    ctx.strokeRect(r.x, r.y, r.width, r.height);
    return;
  }

  if (shape.tool === 'arrow') {
    const [w1, w2] = arrowHeadPoints(shape.from, shape.to);
    ctx.beginPath();
    ctx.moveTo(shape.from.x, shape.from.y);
    ctx.lineTo(shape.to.x, shape.to.y);
    ctx.moveTo(w1.x, w1.y);
    ctx.lineTo(shape.to.x, shape.to.y);
    ctx.lineTo(w2.x, w2.y);
    ctx.stroke();
    return;
  }

  ctx.beginPath();
  shape.points.forEach((p, i) => (i === 0 ? ctx.moveTo(p.x, p.y) : ctx.lineTo(p.x, p.y)));
  ctx.stroke();
}

type Props = {
  /** The captured screen. Drawn as the base layer on every repaint. */
  source: HTMLCanvasElement;
  /**
   * Where the reporter's viewport was, as a fraction of the image on each axis.
   * A full-page capture of an invoice grid is several thousand pixels tall and
   * opening it at the top would put the reporter back where the old
   * viewport-only screenshot left them: looking at row 1 — and, on a wide grid,
   * at column 1.
   */
  focus?: { x: number; y: number };
  /**
   * How many of the reporter's viewport widths the capture spans (>= 1). The
   * pane shows one viewport-width at its own width and scrolls for the rest,
   * rather than fitting the whole image across: a capture 1.8x the viewport
   * fitted to this pane would render the app's 12px labels at 7px, which is
   * exactly the text a pricing bug lives in.
   */
  viewportsWide?: number;
};

/** Module-level so the effect below is not re-run by a fresh literal each render. */
const NO_FOCUS = { x: 0, y: 0 };

export const AnnotationCanvas = forwardRef<AnnotationHandle, Props>(({ source, focus = NO_FOCUS, viewportsWide = 1 }, ref) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const viewRef = useRef<HTMLDivElement>(null);
  const [shapes, setShapes] = useState<Shape[]>([]);
  const [draft, setDraft] = useState<Shape | null>(null);
  const [tool, setTool] = useState<Tool>('rect');

  // One repaint path for everything: base image, committed shapes, then the
  // shape being dragged. Redrawing from scratch keeps undo trivial and means a
  // half-drawn shape can never be accidentally committed to the pixels.
  const repaint = useCallback(() => {
    const canvas = canvasRef.current;
    const ctx = canvas?.getContext('2d');
    if (!canvas || !ctx) return;
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    ctx.drawImage(source, 0, 0);
    shapes.forEach((s) => drawShape(ctx, s));
    if (draft) drawShape(ctx, draft);
  }, [source, shapes, draft]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    canvas.width = source.width;
    canvas.height = source.height;
    repaint();
  }, [source, repaint]);

  // Open on the part of the page the reporter was looking at, on both axes.
  // Fractions, not pixels: the image is laid out relative to the pane's width,
  // so its displayed size is whatever that scaling makes it.
  useEffect(() => {
    const view = viewRef.current;
    if (!view) return;
    if (focus.y) view.scrollTop = focus.y * view.scrollHeight;
    if (focus.x) view.scrollLeft = focus.x * view.scrollWidth;
  }, [source, focus]);

  useImperativeHandle(
    ref,
    () => ({
      toBlob: () =>
        new Promise<Blob | null>((resolve) => {
          const canvas = canvasRef.current;
          if (!canvas) return resolve(null);
          canvas.toBlob((blob) => resolve(blob), 'image/png');
        }),
      hasMarks: () => shapes.length > 0,
    }),
    [shapes]
  );

  const pointFromEvent = (e: React.PointerEvent<HTMLCanvasElement>): Point => {
    const canvas = canvasRef.current!;
    const rect = canvas.getBoundingClientRect();
    return toCanvasPoint({ x: e.clientX, y: e.clientY }, rect, canvas.width, canvas.height);
  };

  const onPointerDown = (e: React.PointerEvent<HTMLCanvasElement>) => {
    e.currentTarget.setPointerCapture(e.pointerId);
    const p = pointFromEvent(e);
    setDraft(tool === 'pen' ? { tool: 'pen', points: [p] } : { tool, from: p, to: p });
  };

  const onPointerMove = (e: React.PointerEvent<HTMLCanvasElement>) => {
    if (!draft) return;
    const p = pointFromEvent(e);
    setDraft(draft.tool === 'pen' ? { tool: 'pen', points: [...draft.points, p] } : { ...draft, to: p });
  };

  const onPointerUp = () => {
    if (!draft) return;
    // pruneShapes drops a click that never became a drag. It matters most for
    // "Black out": a 1px black mark looks like nothing happened, so a reporter
    // who believed they had covered a price would send the price.
    setShapes((prev) => pruneShapes([...prev, draft]));
    setDraft(null);
  };

  const activeHint = TOOLS.find((t) => t.id === tool)?.hint || '';

  return (
    <div className="flex flex-col h-full min-h-0 gap-3">
      <div className="flex flex-wrap items-center gap-2 shrink-0">
        {TOOLS.map((t) => (
          <button
            key={t.id}
            type="button"
            title={t.hint}
            onClick={() => setTool(t.id)}
            className={`px-3 py-1.5 rounded-md text-xs font-medium border transition ${
              tool === t.id
                ? 'bg-slate-800 text-white border-slate-800'
                : 'bg-white text-slate-600 border-slate-300 hover:bg-slate-50'
            }`}
          >
            {t.label}
          </button>
        ))}

        <span className="mx-1 h-5 w-px bg-slate-200" aria-hidden />

        <button
          type="button"
          onClick={() => setShapes((prev) => prev.slice(0, -1))}
          disabled={shapes.length === 0}
          className="px-3 py-1.5 rounded-md text-xs font-medium border border-slate-300 bg-white text-slate-600
                     hover:bg-slate-50 disabled:opacity-40 disabled:cursor-not-allowed"
        >
          Undo
        </button>
        <button
          type="button"
          onClick={() => setShapes([])}
          disabled={shapes.length === 0}
          className="px-3 py-1.5 rounded-md text-xs font-medium border border-slate-300 bg-white text-slate-600
                     hover:bg-slate-50 disabled:opacity-40 disabled:cursor-not-allowed"
        >
          Clear
        </button>

        <span className="text-xs text-slate-500 ml-1">{activeHint}</span>
      </div>

      {/* Scaled so that ONE of the reporter's viewport widths fills the pane,
          then scrolled — not fitted to the box, and not fitted to the whole
          image either. A full-page capture is often ten times taller than it is
          wide, so `object-contain` would shrink it to a stripe a few pixels
          across; and a capture that recovered hidden columns is wider than the
          screen it came from, so fitting THAT across would shrink the text of
          every screen by however much the widest table on it overflowed.
          Marks still land in the right place — toCanvasPoint reads the canvas's
          live bounding rect, which already accounts for this pane's scroll. */}
      <div ref={viewRef} className="flex-1 min-h-0 bg-slate-100 rounded-lg overflow-auto">
        <canvas
          ref={canvasRef}
          onPointerDown={onPointerDown}
          onPointerMove={onPointerMove}
          onPointerUp={onPointerUp}
          onPointerCancel={onPointerUp}
          style={{ width: `${Math.max(1, viewportsWide) * 100}%` }}
          className="block h-auto max-w-none cursor-crosshair touch-none"
        />
      </div>
    </div>
  );
});

AnnotationCanvas.displayName = 'AnnotationCanvas';
